/**
 * AuthProvider — JWT-based authentication context.
 *
 * Manages the full token lifecycle through the platform-aware `tokenStore`
 * adapter (`@/auth/tokenStore`):
 *   - Web: tokens live in localStorage (existing behaviour).
 *   - Native (Capacitor): refresh token lives in `@capacitor/preferences`
 *     (OS-protected storage); access token is in-memory only.
 *
 * On native cold-start the access token is by definition missing, so this
 * provider attempts an automatic refresh against the stored refresh token
 * before any authenticated request is fired.
 *
 * Must be placed *inside* LanguageProvider (so auth errors are translatable)
 * and *outside* QueryClientProvider (so auth expiry/logout can clear query cache).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { apiClient, type AuthChangeReason } from "@shared/lib/api/client";
import { logout as apiLogout } from "@apps/auth/frontend/api";
import { tokenStore } from "../auth/tokenStore";
import { native } from "../native/index";
import { getQueryClient } from "./QueryProvider";

// ── Types ──────────────────────────────────────────────────────────

export interface User {
  id: string;
  email: string;
  name?: string;
  role?: string;
  isStaff?: boolean;
  isSuperuser?: boolean;
}

export type AuthStatus = "loading" | "authenticated" | "unauthenticated";

export interface AuthContextValue {
  status: AuthStatus;
  user: User | null;
  /** Store tokens received from login / signup endpoints. */
  login: (accessToken: string, refreshToken: string) => void;
  /** Patch current user display data after profile edits without waiting for token rotation. */
  updateUser: (patch: Partial<User>) => void;
  /** Clear all tokens and user state. */
  logout: () => void;
  /** Force a token refresh. Returns the new access token or null on failure. */
  refresh: () => Promise<string | null>;
  /** Current access token (for use in request headers). */
  accessToken: string | null;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// ── Helpers ────────────────────────────────────────────────────────

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const base64 = token.split(".")[1];
    if (!base64) return null;
    const json = atob(base64.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function userFromToken(token: string | null): User | null {
  if (!token) return null;
  const payload = decodeJwtPayload(token);
  if (!payload) return null;
  return {
    id: String(payload.user_id ?? payload.sub ?? ""),
    email: String(payload.email ?? ""),
    name: payload.name ? String(payload.name) : undefined,
    role: payload.role ? String(payload.role) : undefined,
    isStaff: payload.is_staff === true || payload.isStaff === true,
    isSuperuser: payload.is_superuser === true || payload.isSuperuser === true,
  };
}

function secondsUntilExpiry(token: string): number {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== "number") return 0;
  return Math.max(0, payload.exp - Date.now() / 1000);
}

// ── Provider ───────────────────────────────────────────────────────

/** Refresh margin: attempt refresh 60 s before expiry. */
const REFRESH_MARGIN_S = 60;

export function AuthProvider({ children }: { children: ReactNode }) {
  // `apiClient.getAccessToken()` is sync and reads from the active store.
  const [accessToken, setAccessToken] = useState<string | null>(
    () => apiClient.getAccessToken(),
  );
  const [status, setStatus] = useState<AuthStatus>(() =>
    native.isNative ? "loading" : apiClient.getAccessToken() ? "authenticated" : "unauthenticated",
  );
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [userOverride, setUserOverride] = useState<Partial<User> | null>(null);

  const tokenUser = useMemo(() => userFromToken(accessToken), [accessToken]);
  const user = useMemo(() => {
    if (!tokenUser) return null;
    if (userOverride?.id && userOverride.id !== tokenUser.id) return tokenUser;
    return userOverride ? { ...tokenUser, ...userOverride } : tokenUser;
  }, [tokenUser, userOverride]);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const syncAuthState = useCallback(
    (token: string | null, reason?: AuthChangeReason) => {
      setAccessToken(token);
      setStatus(token ? "authenticated" : "unauthenticated");

      if (!token || reason === "logout" || reason === "expired") {
        setUserOverride(null);
        clearRefreshTimer();
        getQueryClient().clear();
      }
    },
    [clearRefreshTimer],
  );

  const clearAuth = useCallback(
    async (reason: AuthChangeReason = "logout") => {
      await apiClient.clearAuth(reason);
      syncAuthState(null, reason);
    },
    [syncAuthState],
  );

  const refresh = useCallback(async (): Promise<string | null> => {
    const newAccess = await apiClient.refreshAccessToken();
    if (!newAccess) {
      syncAuthState(null, "expired");
      return null;
    }
    syncAuthState(newAccess, "refresh");
    return newAccess;
  }, [syncAuthState]);

  useEffect(() => {
    return apiClient.onAuthChange(({ accessToken: nextAccessToken, reason }) => {
      syncAuthState(nextAccessToken, reason);
    });
  }, [syncAuthState]);

  const scheduleRefresh = useCallback(
    (token: string) => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      const ttl = secondsUntilExpiry(token);
      if (ttl <= 0) {
        void refresh();
        return;
      }
      const delayMs = Math.max(0, (ttl - REFRESH_MARGIN_S) * 1000);
      refreshTimerRef.current = setTimeout(() => void refresh(), delayMs);
    },
    [refresh],
  );

  useEffect(() => {
    if (accessToken) scheduleRefresh(accessToken);
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [accessToken, scheduleRefresh]);

  // On mount: validate existing token AND run native cold-start.
  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      // Native: migrate legacy WebView localStorage tokens once.
      if (native.isNative) {
        await tokenStore.migrateFromWebStorageIfNeeded();
      }

      const inMemoryAccess = apiClient.getAccessToken();
      if (inMemoryAccess && secondsUntilExpiry(inMemoryAccess) > 0) {
        if (!cancelled) {
          syncAuthState(inMemoryAccess, "login");
        }
        return;
      }

      // Native cold-start: access is gone, try the persisted refresh.
      if (native.isNative) {
        const refreshTkn = await tokenStore.getRefresh();
        if (!refreshTkn) {
          if (!cancelled) syncAuthState(null, "expired");
          return;
        }
        const newAccess = await apiClient.refreshAccessToken();
        if (!cancelled) {
          syncAuthState(newAccess, newAccess ? "refresh" : "expired");
        }
        return;
      }

      // Web: an expired access token still has a refresh in localStorage.
      if (inMemoryAccess && secondsUntilExpiry(inMemoryAccess) <= 0) {
        const newAccess = await apiClient.refreshAccessToken();
        if (!cancelled) {
          syncAuthState(newAccess, newAccess ? "refresh" : "expired");
        }
        return;
      }

      if (!cancelled) syncAuthState(null, "expired");
    };

    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, [syncAuthState]);

  const login = useCallback(
    (access: string, refreshTkn: string) => {
      void apiClient.setAuthTokens(access, refreshTkn, "login");
      setUserOverride(null);
      syncAuthState(access, "login");
      scheduleRefresh(access);
    },
    [scheduleRefresh, syncAuthState],
  );

  const updateUser = useCallback((patch: Partial<User>) => {
    setUserOverride((current) => ({ ...current, ...patch }));
  }, []);

  const logout = useCallback(() => {
    void (async () => {
      const refreshToken = await apiClient.getRefreshToken();
      if (refreshToken) {
        try {
          await apiLogout(refreshToken);
        } catch {
          // Local logout still wins even if the backend blacklist fails.
        }
      }
      await clearAuth();
    })();
  }, [clearAuth]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, login, updateUser, logout, refresh, accessToken }),
    [status, user, login, updateUser, logout, refresh, accessToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
